/**
 * Browser upload: presigned POST straight to S3 (never through the API),
 * then registration so the server can verify the object and start processing.
 */
import { UPLOAD_TYPES, type UploadContentType } from "@yushi-portfolio/api-types";
import type { Asset } from "@yushi-portfolio/content-schema";
import type { ApiClient } from "./api";

/** For <input accept>. Listing JPEG (not image/*) makes iOS hand over HEIC photos as JPEG. */
export const ACCEPT = Object.keys(UPLOAD_TYPES).join(",");

export function checkFile(file: File): string | undefined {
  if (file.type === "video/quicktime") return `${file.name}: MOV は Web で再生できない場合があります。MP4 (H.264) で書き出してからアップロードしてください。`;
  if (file.type === "image/heic" || file.type === "image/heif") return `${file.name}: HEIC は非対応です。JPEG に変換してください。`;
  const policy = UPLOAD_TYPES[file.type as UploadContentType];
  if (!policy) return `${file.name}: 対応していない形式です (JPEG, PNG, TIFF, WebP, MP4, WebM, PDF)。`;
  if (file.size > policy.maxBytes) return `${file.name}: ファイルが大きすぎます (上限 ${Math.round(policy.maxBytes / 1024 ** 2)} MB)。`;
  return undefined;
}

function videoMetadata(file: File): Promise<{ width?: number; height?: number; durationSeconds?: number }> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (value: { width?: number; height?: number; durationSeconds?: number }) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.preload = "metadata";
    video.onloadedmetadata = () =>
      done({
        width: video.videoWidth || undefined,
        height: video.videoHeight || undefined,
        durationSeconds: Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : undefined,
      });
    video.onerror = () => done({});
    video.src = url;
  });
}

function postForm(url: string, form: FormData, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload failed (HTTP ${xhr.status})`)));
    xhr.onerror = () => reject(new Error("upload failed (network error)"));
    xhr.send(form);
  });
}

export async function uploadFile(api: ApiClient, file: File, onProgress: (fraction: number) => void): Promise<Asset> {
  const problem = checkFile(file);
  if (problem) throw new Error(problem);
  const contentType = file.type as UploadContentType;

  const upload = await api.createUpload({ contentType, size: file.size });
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value);
  form.append("file", file); // must be the last field
  await postForm(upload.url, form, onProgress);

  const meta = UPLOAD_TYPES[contentType].type === "video" ? await videoMetadata(file) : {};
  return api.registerAsset({ assetId: upload.assetId, contentType, originalFilename: file.name, ...meta });
}

/** Smallest browser-friendly still for thumbnails (a JPEG/WebP variant, incl. video posters). */
export function thumbnailUrl(asset: Asset, mediaBaseUrl: string): string | undefined {
  const still = (asset.variants ?? [])
    .filter((v) => v.format === "jpeg" || v.format === "webp")
    .sort((a, b) => (a.width ?? 0) - (b.width ?? 0))[0];
  return still ? `${mediaBaseUrl}/${still.key}` : undefined;
}

export function assetStatus(asset: Asset): "ready" | "processing" | "stored" {
  if (asset.type === "document") return "stored";
  return asset.variants?.length ? "ready" : "processing";
}
